import {
  Container,
  Grid,
  InputAdornment,
  Slider,
  Tooltip,
  Stack,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ChangeEvent,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { capitalize, getCountryFromStateCode, TIME_ZONES } from 'utils';
import { useTranslation } from 'react-i18next';
import {
  Contact,
  PhoneType,
  Rule,
  Status,
  Tenant,
  TenantData,
  TenantPublicContactAndData,
  TimeZone,
  TraxeroActionResponse,
} from 'types';
import { ThemeContext } from 'context/themeContext';
import ColorPicker from 'components/inputs/ColorPicker';
import { ACTION_TYPE, AppContext } from 'context/appContext';
import {
  ActionFunction,
  LoaderFunctionArgs,
  useFetcher,
  useLoaderData,
} from 'react-router';
import CompanyPathInput from 'components/inputs/CompanyPath';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import AuthService from 'utils/AuthService';
import { getBlankContact } from 'utils/emptyEntities';
import { ValidatorForm } from 'react-material-ui-form-validator';
import FormFrame from 'components/layout/FormFrame';
import FormHeaderBar from 'components/layout/FormHeaderBar';
import DetailsHeader from 'components/layout/DetailsHeader';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import SettingsSection from 'components/layout/SettingsSection';
import AuturaTextField from 'components/inputs/AuturaTextField';
import AuturaPhoneField from 'components/inputs/AuturaPhoneField';
import AuturaStateField from 'components/inputs/AuturaStateField';
import { getTenantRules, updateTenantPublicContactAndData } from 'api';
import { useFetcherKey } from 'hooks/useFetcherKey';
import AuturaSwitch from 'components/inputs/AuturaSwitch';
import { DEFAULT_PROPERTY_LABELS } from 'components/autocompletes/FilterLabelAutocomplete';
import TimeZoneSelect from 'components/inputs/TimeZoneSelect';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import { AddOutlined } from '@mui/icons-material';
import LoginPreview from './LoginPreview';

interface CompanyDetailsLoaderData {
  _rules: Promise<Rule[]>;
}
export const companyDetailsLoader = ({
  params,
}: LoaderFunctionArgs): CompanyDetailsLoaderData => {
  return {
    _rules: getTenantRules(Number(params.tenantID)),
  };
};

const CompanyDetails = (): ReactNode => {
  const { updatePalette, setBannerLogo, setTenantBranding } =
    useContext(ThemeContext);
  const { state, dispatch } = useContext(AppContext);
  const displayToast = useContext(ToastContext);

  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const { _rules } = useLoaderData() as CompanyDetailsLoaderData;
  const { t } = useTranslation();
  const canEdit = AuthService.IsSuperAdmin() || AuthService.IsAdmin();
  const [tenant, setTenant] = useState<Tenant>(state.tenant);
  const [isAvailable, setIsAvailable] = useState<boolean>(true);
  const [labelInput, setLabelInput] = useState<string>('');
  const [filterLabels, setFilterLabels] = useState<string[]>(
    tenant.data?.filterLabels || DEFAULT_PROPERTY_LABELS,
  );
  const [defaultRuleID, setDefaultRuleID] = useState<number>(
    tenant.data?.defaultRuleID || 0,
  );
  const [timeZone, setTimeZone] = useState<TimeZone>(
    TIME_ZONES.find(
      (timeZone: TimeZone) =>
        timeZone.label === tenant.data?.timeZone ||
        timeZone.timeZone === state.tenant.data?.timeZone,
    ) || TIME_ZONES[0],
  );
  const [tenantRules, setTenantRules] = useState<Rule[]>([]);
  const [data, setData] = useState<TenantData>(tenant.data);
  const [publicContactInfo, setPublicContactInfo] = useState<Contact>(
    tenant.publicContactInfo || getBlankContact(tenant.id),
  );

  useEffect(() => {
    const awaitLoader = async () => {
      const rules = await _rules;
      setTenantRules(rules);
    };
    awaitLoader();
  }, [_rules]);

  // Function to reset all state variables to their original values
  const resetState = () => {
    setDefaultRuleID(state.tenant.data?.defaultRuleID || 0);
    setLabelInput('');
    setTenant(state.tenant);
  };

  useEffect(() => {
    if (fetcher.data) {
      const response = fetcher.data as TraxeroActionResponse<string>;
      if (response.status === 200) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: t('update_success', {
            ns: 'toasts',
            name: `${tenant.name} ${t('settings')}`,
          }),
        });
        updateTenantInAppState();
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: t('update_error', {
            ns: 'toasts',
            name: `${tenant.name} ${t('settings')}`,
            error: response.statusText,
          }),
        });
      }
    }
  }, [fetcher.data]);

  const updateTenantInAppState = () => {
    const updatedTenant: Tenant = {
      ...tenant,
      publicContactInfo: publicContactInfo,
      data: {
        ...data,
        branding: data.branding,
        timeZone: timeZone.timeZone,
        filterLabels: filterLabels,
        defaultRuleID: defaultRuleID,
        entityMerchant: data.entityMerchant,
      },
    };

    const userTenantsWithoutUpdatedTenant = state.userTenants.filter((tenant) => tenant.id !== updatedTenant.id);
    const userTenantsWithUpdatedTenant = [...userTenantsWithoutUpdatedTenant, updatedTenant].sort((a, b) => a.id - b.id);

    dispatch({
      type: ACTION_TYPE.SET_USER_TENANTS,
      payload: userTenantsWithUpdatedTenant,
    });

    dispatch({
      type: ACTION_TYPE.SET_ACTIVE_TENANT,
      payload: updatedTenant,
    });
    setTenantBranding({
      branding: data.branding,
      companyPath: data.companyPath,
    });
    updatePalette({
      primary: data.branding?.primary,
      secondary: data.branding?.secondary,
    });
    setBannerLogo({
      url: data.branding?.bannerLogoUrl,
      padding: data.branding?.bannerPadding || 0,
    });
    setTenant({
      ...tenant,
      data: updatedTenant.data,
      publicContactInfo: updatedTenant.publicContactInfo,
    });
  };

  const handleSubmit = () => {
    const tenantData: TenantData = {
      ...data,
      timeZone: timeZone.timeZone,
      entityMerchant: data.entityMerchant || {
        merchantID: null,
        status: Status.Disabled,
        data: {},
      },
      branding: data.branding,
      companyPath: data.companyPath,
      enableFindMyCar: data?.enableFindMyCar || false,
      filterLabels,
      defaultRuleID,
    };

    const dataAndContact: TenantPublicContactAndData = {
      data: tenantData,
      contact: publicContactInfo,
    };

    fetcher.submit(
      {
        body: JSON.stringify(dataAndContact),
        tenantID: state.tenant.id,
      },
      {
        method: 'post',
        encType: 'application/json',
      },
    );
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    switch (name) {
      case 'publicContactInfo.location.street':
        setPublicContactInfo({
          ...publicContactInfo,
          id: publicContactInfo.id,
          name: publicContactInfo.name,
          status: publicContactInfo.status,
          tenantID: tenant.id,
          location: {
            ...publicContactInfo.location,
            street: value,
          },
        });
        break;
      case 'publicContactInfo.location.city':
        setPublicContactInfo({
          ...publicContactInfo,
          id: publicContactInfo.id,
          name: publicContactInfo.name,
          status: publicContactInfo.status,
          tenantID: tenant.id,
          location: {
            ...publicContactInfo.location,
            city: value,
          },
        });
        break;
      case 'publicContactInfo.location.postalCode':
        setPublicContactInfo({
          ...publicContactInfo,
          id: publicContactInfo.id,
          name: publicContactInfo.name,
          status: publicContactInfo.status,
          tenantID: tenant.id,
          location: {
            ...publicContactInfo.location,
            postalCode: value,
          },
        });
        break;
      case 'publicContactInfo.location.unit':
        setPublicContactInfo({
          ...publicContactInfo,
          id: publicContactInfo.id,
          name: publicContactInfo.name,
          status: publicContactInfo.status,
          tenantID: tenant.id,
          location: {
            ...publicContactInfo.location,
            unit: value,
          },
        });
        break;
      case 'publicContactInfo.primaryEmail':
        setPublicContactInfo({
          ...publicContactInfo,
          id: publicContactInfo.id,
          name: publicContactInfo.name,
          status: publicContactInfo.status,
          tenantID: tenant.id,
          primaryEmail: value,
          location: { ...tenant.publicContactInfo?.location },
        });
        break;
      case 'publicContactInfo.website':
        setPublicContactInfo({
          ...publicContactInfo,
          id: publicContactInfo.id,
          name: publicContactInfo.name,
          status: publicContactInfo.status,
          tenantID: tenant.id,
          website: value,
          location: {
            ...publicContactInfo.location,
          },
        });
        break;
      case 'findMyCarMsg':
        setData({
          ...data,
          findMyCarMsg: value,
        });
        break;
    }
  };
  const handleLabelCreate = () => {
    setFilterLabels([...filterLabels, labelInput.toLowerCase()]);
    setLabelInput('');
  };
  const handleLabelRemove = (label: string) => {
    setFilterLabels(filterLabels.filter((l) => l !== label));
  };
  const handleTimeZoneChange = (timeZone: TimeZone) => {
    setTimeZone(timeZone);
  };
  const handleDefaultRuleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rule = tenantRules.find((rule: Rule) => rule.name === e.target.value);
    setDefaultRuleID(rule?.id || 0);
  };
  const defaultRule = useMemo(() => {
    return (
      tenantRules.find((rule: Rule) => rule.id === defaultRuleID) ||
      tenantRules[0]
    );
  }, [defaultRuleID, tenantRules]);

  useEffect(() => {
    updatePalette({
      primary: data.branding?.primary,
      secondary: data.branding?.secondary,
    });

    if (data?.branding?.bannerLogoUrl) {
      setBannerLogo({
        url: data.branding.bannerLogoUrl,
        padding: data?.branding?.bannerPadding || 0,
      });
    }
  }, [data.branding]);

  return (
    <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
      <FormHeaderBar />
      <FormFrame loading={fetcher.state === 'submitting'}>
        <DetailsHeader
          title={t('tabMenu.company-setup')}
          onCancel={() => resetState()}
          submitLabel={t('buttons.update', {
            name: t('tabMenu.company-setup'),
          })}
          disableSubmit={fetcher.state !== 'idle'}
          readonly={!state.entityPermissions.tenant.update}
        />
        <ScrollableFormContainer maxHeight={`calc(100dvh - 326px)`}>
          <SettingsSection
            title={t('tenant.publicContactInfo.label', { ns: 'entities' })}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('tenant.publicContactInfo.primaryEmail', {
                    ns: 'entities',
                  })}
                  name={'publicContactInfo.primaryEmail'}
                  value={publicContactInfo?.primaryEmail || ''}
                  validators={['isEmail']}
                  errorMessages={['Invalid email']}
                  onChange={handleChange}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  label={t(`tenant.publicContactInfo.primaryPhone`, {
                    ns: 'entities',
                  })}
                  name={'publicContactInfo.primaryPhone'}
                  typeFieldName={'tenant.publicContactInfo.primaryPhoneType'}
                  typeFieldLabel='Type'
                  phoneNumber={publicContactInfo?.primaryPhone || ''}
                  setPhoneNumber={(phone: string) => {
                    setPublicContactInfo({
                      ...publicContactInfo,
                      id: publicContactInfo?.id || 0,
                      name: tenant.publicContactInfo?.name || 'public contact',
                      status: tenant.publicContactInfo?.status || Status.Active,
                      tenantID: tenant.id,
                      primaryPhone: phone,
                      location: {
                        ...tenant?.publicContactInfo?.location,
                      },
                    });
                  }}
                  phoneType={
                    publicContactInfo?.primaryPhoneType || PhoneType.Office
                  }
                  setPhoneType={(phoneType: PhoneType) => {
                    setPublicContactInfo({
                      ...publicContactInfo,
                      id: publicContactInfo?.id || 0,
                      name: tenant.publicContactInfo?.name || 'public contact',
                      status: tenant.publicContactInfo?.status || Status.Active,
                      tenantID: tenant.id,
                      primaryPhoneType: phoneType,
                      location: {
                        ...tenant?.publicContactInfo?.location,
                      },
                    });
                  }}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  label={t('tenant.publicContactInfo.website', {
                    ns: 'entities',
                  })}
                  name={'publicContactInfo.website'}
                  value={publicContactInfo?.website || ''}
                  readonly={!canEdit}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  label={t('tenant.publicContactInfo.location.street', {
                    ns: 'entities',
                  })}
                  name='publicContactInfo.location.street'
                  value={publicContactInfo?.location?.street || ''}
                  onChange={handleChange}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('tenant.publicContactInfo.location.unit', {
                    ns: 'entities',
                  })}
                  name='publicContactInfo.location.unit'
                  value={publicContactInfo?.location?.unit || ''}
                  onChange={handleChange}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('tenant.publicContactInfo.location.city', {
                    ns: 'entities',
                  })}
                  name='publicContactInfo.location.city'
                  value={publicContactInfo?.location?.city || ''}
                  onChange={handleChange}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaStateField
                  stateCode={publicContactInfo?.location?.stateCode || ''}
                  setStateCode={(stateCode: string) => {
                    setPublicContactInfo({
                      ...publicContactInfo,
                      id: publicContactInfo.id,
                      name: publicContactInfo.name,
                      status: publicContactInfo.status,
                      tenantID: publicContactInfo.tenantID,
                      location: {
                        ...publicContactInfo.location,
                        stateCode: stateCode,
                        country: getCountryFromStateCode(stateCode),
                      },
                    });
                  }}
                  label={t('tenant.publicContactInfo.location.stateCode', {
                    ns: 'entities',
                  })}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('tenant.publicContactInfo.location.postalCode', {
                    ns: 'entities',
                  })}
                  name='publicContactInfo.location.postalCode'
                  value={publicContactInfo?.location?.postalCode || ''}
                  onChange={handleChange}
                  readonly={!canEdit}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.company_settings', {
              ns: 'tenantSettings',
            })}
          >
            <Grid container width={'100%'} spacing={1}>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='verifyNumberCharactersVIN'
                  label={t('tenant.data.verifyNumberCharactersVIN', {
                    ns: 'entities',
                  })}
                  helperText={t('helpers.vin_verify', {
                    value: data.verifyNumberCharactersVIN || '0',
                    ns: 'tenantSettings',
                  })}
                  value={data.verifyNumberCharactersVIN || '0'}
                  readonly={!canEdit}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setData({
                      ...data,
                      verifyNumberCharactersVIN: parseInt(e.target.value),
                    });
                  }}
                  validators={['isNumber']}
                  errorMessages={['Invalid Input']}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <TimeZoneSelect
                  sx={{ width: '100%' }}
                  name='timeZone'
                  inputLabel={`${t('tenant.data.timeZone', {
                    ns: 'entities',
                  })}`}
                  value={timeZone}
                  setValue={handleTimeZoneChange}
                  required
                />
              </Grid>
              {tenantRules.length > 0 && (
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                    lg: 3,
                  }}
                >
                  <AuturaDropdown
                    name='defaultRuleID'
                    label={`${t('tenant.data.defaultRule', {
                      ns: 'entities',
                    })}`}
                    options={tenantRules.map((rule: Rule) => rule.name)}
                    value={defaultRule?.name || ''}
                    setValue={handleDefaultRuleChange}
                    resolve={_rules}
                    helperText={capitalize(
                      t('helpers.default_rule', {
                        ns: 'tenantSettings',
                      }),
                    )}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                {state.features.allowJobCancellation && (
                <AuturaDropdown
                  value={data.allowJobCancellationBeforeStatus || t('labels.never', { ns: 'tenantSettings' })}
                  name={'allowJobCancellationBeforeStatus'}
                  label={t(
                    'tenant.data.allowJobCancellationBeforeStatus.label',
                    { ns: 'entities' },
                  )}
                  helperText={t('helpers.job_cancellation_subheading', {
                    ns: 'tenantSettings',
                  })}
                  readonly={!canEdit}
                  options={[
                    t('labels.never', { ns: 'tenantSettings' }),
                    t('labels.enroute', { ns: 'tenantSettings' }),
                    t('labels.arrived', { ns: 'tenantSettings' }),
                    t('labels.hooked', { ns: 'tenantSettings' }),
                  ]}
                  setValue={(e) => {
                    setData({
                      ...data,
                      allowJobCancellationBeforeStatus: e.target.value,
                    });
                  }}
                />
                )}
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  onChange={(e) => setLabelInput(e.target.value)}
                  value={labelInput}
                  name={'filterLabelInput'}
                  label={t('tenant.data.filterLabels', {
                    ns: 'entities',
                  })}
                  helperText={t('helpers.filter_label', {
                    ns: 'tenantSettings',
                  })}
                  endAdornment={
                    <InputAdornment
                      position='end'
                      sx={{ p: 0, ml: 0, height: '100%' }}
                    >
                      <IconButton
                        onClick={handleLabelCreate}
                        size={'small'}
                        sx={{ p: 0, m: 0 }}
                        disabled={!labelInput}
                        color={'primary'}
                      >
                        <AddOutlined color={'inherit'} fontSize={'small'} />
                      </IconButton>
                    </InputAdornment>
                  }
                  variant='outlined'
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 9,
                }}
              >
                <Stack
                  display={'flex'}
                  direction={'row'}
                  flexWrap={'wrap'}
                  gap={1}
                  justifyContent={'flex-start'}
                  alignItems={'center'}
                  sx={{ width: '100%', height: '100%', m: 0, pb: 2 }}
                >
                  {filterLabels.map((l: string, index: number) => {
                    return (
                      <Chip
                        component={'li'}
                        key={`label-${l}-${index}`}
                        label={l}
                        variant='outlined'
                        onDelete={() => handleLabelRemove(l)}
                      />
                    );
                  })}
                </Stack>
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.towpay_integration', {
              ns: 'tenantSettings',
            })}
            subtitle={t('helpers.towpay_integration', { ns: 'tenantSettings' })}
          >
            <Grid container width={'100%'} spacing={1}>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='towpayMerchantID'
                  label={t('tenant.data.entityMerchant.merchantID', {
                    ns: 'entities',
                  })}
                  value={data.entityMerchant?.merchantID || ''}
                  disabled={data.entityMerchant?.status === Status.Disabled}
                  readonly={!canEdit}
                  onChange={(e) =>
                    setData({
                      ...data,
                      entityMerchant: {
                        status: data.entityMerchant?.status || Status.Active,
                        merchantID: e.target.value,
                        data: data.entityMerchant?.data || {},
                      },
                    })
                  }
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaSwitch
                  sx={{ pt: 1 }}
                  value={data.entityMerchant?.status === Status.Active}
                  name={'enableTowpay'}
                  label={t('tenant.data.entityMerchant.status', {
                    ns: 'entities',
                  })}
                  readonly={!canEdit}
                  setValue={(value: boolean) => {
                    setData({
                      ...data,
                      entityMerchant: {
                        status: value ? Status.Active : Status.Disabled,
                        merchantID: value
                          ? data.entityMerchant?.merchantID || ''
                          : '',
                        data: data.entityMerchant?.data || {},
                      },
                    });
                  }}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.find_my_car', { ns: 'tenantSettings' })}
            subtitle={t('helpers.find_my_car_subheading', {
              ns: 'tenantSettings',
            })}
          >
            <Grid container width={'100%'} spacing={1.5} sx={{ mt: -1 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <AuturaSwitch
                  sx={{ maxWidth: 500 }}
                  value={data.enableFindMyCar || false}
                  name={'enableFindMyCar'}
                  label={t('tenant.data.enableFindMyCar.label', {
                    ns: 'entities',
                  })}
                  helperText={t('helpers.enable_fmc', {
                    ns: 'tenantSettings',
                  })}
                  readonly={!canEdit}
                  setValue={(value: boolean) => {
                    setData({
                      ...data,
                      enableFindMyCar: value,
                    });
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <AuturaSwitch
                  // sx={{ maxWidth: 500 }}
                  value={data.useDefaultPublicContact || false}
                  name={'useDefaultPublicContact'}
                  label={t('tenant.data.useDefaultPublicContact', {
                    ns: 'entities',
                  })}
                  readonly={!canEdit}
                  disabled={!data.enableFindMyCar}
                  helperText={t('helpers.use_tenant_contact', {
                    ns: 'tenantSettings',
                  })}
                  setValue={(value: boolean) => {
                    setData({
                      ...data,
                      useDefaultPublicContact: value,
                    });
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 8 }} mt={2}>
                <AuturaTextField
                  name='findMyCarMsg'
                  label={t('tenant.data.findMyCarMsg', {
                    ns: 'entities',
                  })}
                  multiline={true}
                  rows={2}
                  value={data.findMyCarMsg || ''}
                  onChange={handleChange}
                  readonly={!canEdit}
                  disabled={!data.enableFindMyCar}
                  helperText={t('helpers.find_my_car_msg', {
                    ns: 'tenantSettings',
                  })}
                  maxLength={255}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('tenant.data.branding.header', { ns: 'entities' })}
            divider={false}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 8,
                }}
              >
                <Grid py={1} size={12}>
                  <CompanyPathInput
                    companyPath={data.companyPath || ''}
                    setCompanyPath={(companyPath: string) => {
                      setData({
                        ...data,
                        companyPath: companyPath,
                      });
                    }}
                    isAvailable={isAvailable}
                    setIsAvailable={setIsAvailable}
                  />
                </Grid>
                <Grid py={1} size={12}>
                  <AuturaTextField
                    name={`tenant.data.branding.logoUrl`}
                    label={t('tenant.data.branding.logoUrl', {
                      ns: 'entities',
                    })}
                    helperText={t('helpers.logo_url', { ns: 'tenantSettings' })}
                    endAdornment={
                      <InputAdornment position='end' sx={{ pl: 1 }}>
                        <Slider
                          min={0}
                          max={64}
                          sx={{ width: 64 }}
                          valueLabelDisplay={'on'}
                          valueLabelFormat={(value) => `Padding: ${value}`}
                          slots={{
                            valueLabel: ({ value, children }) => (
                              <Tooltip placement='top' title={value}>
                                {children}
                              </Tooltip>
                            ),
                          }}
                          aria-label='logo margin'
                          value={data.branding?.logoPadding || 0}
                          onChange={(e, value) =>
                            setData({
                              ...data,
                              branding: {
                                primary: data.branding?.primary || '#000000',
                                secondary:
                                  data.branding?.secondary || '#000000',
                                bannerLogoUrl:
                                  data.branding?.bannerLogoUrl || '',
                                bannerPadding:
                                  data.branding?.bannerPadding || 0,
                                logoUrl: data.branding?.logoUrl || '',
                                logoPadding: value as number,
                              },
                            })
                          }
                        />
                      </InputAdornment>
                    }
                    value={data.branding?.logoUrl || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setData({
                        ...data,
                        branding: {
                          primary: data.branding?.primary || '#000000',
                          secondary: data.branding?.secondary || '#000000',
                          bannerLogoUrl: data.branding?.bannerLogoUrl || '',
                          bannerPadding: data.branding?.bannerPadding || 0,
                          logoUrl: e.target.value,
                          logoPadding: data.branding?.logoPadding || 0,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid py={1} size={12}>
                  <AuturaTextField
                    name={'tenant.data.branding.bannerLogoUrl'}
                    label={t('tenant.data.branding.bannerLogoUrl', {
                      ns: 'entities',
                    })}
                    helperText={t('helpers.banner_logo_url', {
                      ns: 'tenantSettings',
                    })}
                    value={data.branding?.bannerLogoUrl || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setData({
                        ...data,
                        branding: {
                          primary: data.branding?.primary || '#000000',
                          secondary: data.branding?.secondary || '#000000',
                          bannerLogoUrl: e.target.value,
                          bannerPadding: data.branding?.bannerPadding || 0,
                          logoUrl: data.branding?.logoUrl || '',
                          logoPadding: data.branding?.logoPadding || 0,
                        },
                      })
                    }
                    endAdornment={
                      <InputAdornment position='end' sx={{ pl: 1 }}>
                        <Slider
                          min={0}
                          max={64}
                          sx={{ width: 64 }}
                          valueLabelDisplay={'on'}
                          color={'primary'}
                          valueLabelFormat={(value) => `Padding: ${value}`}
                          slots={{
                            valueLabel: ({ value, children }) => (
                              <Tooltip placement='top' title={value}>
                                {children}
                              </Tooltip>
                            ),
                          }}
                          aria-label='banner logo padding'
                          value={data.branding?.bannerPadding || 0}
                          onChange={(e, value) =>
                            setData({
                              ...data,
                              branding: {
                                primary: data.branding?.primary || '#000000',
                                secondary:
                                  data.branding?.secondary || '#000000',
                                bannerLogoUrl:
                                  data.branding?.bannerLogoUrl || '',
                                bannerPadding: value as number,
                                logoUrl: data.branding?.logoUrl || '',
                                logoPadding: data.branding?.logoPadding || 0,
                              },
                            })
                          }
                        />
                      </InputAdornment>
                    }
                  />
                </Grid>
                <Grid py={1} size={12}>
                  <ColorPicker
                    color={data.branding?.secondary || '#000000'}
                    setColor={(color: string) => {
                      setData({
                        ...data,
                        branding: {
                          primary: data.branding?.primary || '#000000',
                          secondary: color,
                          bannerLogoUrl: data.branding?.bannerLogoUrl || '',
                          bannerPadding: data.branding?.bannerPadding || 0,
                          logoUrl: data.branding?.logoUrl || '',
                          logoPadding: data.branding?.logoPadding || 0,
                        },
                      });
                    }}
                    label={t('tenant.data.branding.secondary', {
                      ns: 'entities',
                    })}
                  />
                  <ColorPicker
                    color={data.branding?.primary || '#000000'}
                    setColor={(color: string) => {
                      setData({
                        ...data,
                        branding: {
                          primary: color,
                          secondary: data.branding?.secondary || '#000000',
                          bannerLogoUrl: data.branding?.bannerLogoUrl || '',
                          bannerPadding: data.branding?.bannerPadding || 0,
                          logoUrl: data.branding?.logoUrl || '',
                          logoPadding: data.branding?.logoPadding || 0,
                        },
                      });
                    }}
                    label={t('tenant.data.branding.primary', {
                      ns: 'entities',
                    })}
                  />
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 4,
                }}
              >
                <Container
                  component={'main'}
                  maxWidth={'sm'}
                  sx={{
                    pb: 4,
                  }}
                >
                  <LoginPreview
                    logoUrl={data.branding?.logoUrl || ''}
                    logoPadding={data.branding?.logoPadding || 0}
                  />
                </Container>
              </Grid>
            </Grid>
          </SettingsSection>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};

export const companyDetailsAction: ActionFunction = async ({
  request,
}): Promise<TraxeroActionResponse<string>> => {
  const { body, tenantID } = await request.json();
  let res: Response;

  try {
    res = await updateTenantPublicContactAndData({
      tenantID: tenantID,
      body: body,
    });
    if (res.status === 200) {
      return { handler: 'UPDATE', status: res.status, statusText: '' };
    }

    const error = await res.json();
    return { handler: 'UPDATE', status: res.status, statusText: error.message };
  } catch (error) {
    if (error instanceof Error) {
      return { handler: 'UPDATE', status: 500, statusText: error.message };
    }
    return {
      handler: 'UPDATE',
      status: 500,
      statusText: 'Internal server error',
    };
  }
};

export default CompanyDetails;
